import React from 'react';
import { loadableReady } from '@loadable/component';
import { checkUserAuthentication } from 'Douleutaras/lib/user-auth';
import 'Douleutaras/lib/i18n'; // Internationalization
import { COUNTRY_CONFIG } from 'Douleutaras/constants/localization';
import { useLocalization } from 'Douleutaras/hooks/localization';
import { createUserAgentCookie } from 'Douleutaras/app/lib/mobileUtilities';
import { createRender } from 'Douleutaras/app/render';
import storeMarketingTags from 'Douleutaras/lib/marketing-tags-storage';

import { store } from '../redux/store';

import localStorage from './lib/storageWrapper';
import { initializeLogging } from './lib/logging';

declare global {
  interface Window {
    dataLayer: Array<string>;
  }
}

loadableReady(async () => {

  // This is a mock server required for development
  if (process.env.REAL_ENV !== 'production' && Boolean(process.env.ENABLE_MOCK_SERVER)) {
    // tslint:disable-next-line:no-require-imports
    const { worker } = require('Douleutaras/api/mocks/browser');
    await worker.start();
  }

  // Creates a cookie based on UserAgent. This is used in the mobile app to transfer userAgent to analytics
  createUserAgentCookie();

  // Store marketing tags
  storeMarketingTags();

  const authentication = await checkUserAuthentication(
    localStorage(),
    { dispatch: store.dispatch }
  );

  initializeLogging({
    user: authentication.currentUser
  });

  let pusherChannel;
  if (authentication.jwtToken) {
    // Dynamically imported: pusher-js only needs to ship to logged-in visitors.
    const { pusherInitializer } = await import('../providers/pusher');
    const pusher = pusherInitializer({ jwtToken: authentication.jwtToken });
    const API_URL = COUNTRY_CONFIG.API_URL[(useLocalization()).countryCode]
      .replace(/(^https?\:\/\/(www\.)?)/, '')
      .replace(/\/$/, '');
    if (authentication.currentUser?.id) {
      pusherChannel = pusher.subscribe(`private-${API_URL}_user_${authentication.currentUser.id}`);
    }
  }

  const container = document.querySelector('#app');
  createRender(container, store, pusherChannel);

  // Remove SSR-injected inline styles once external CSS <link> tags have loaded,
  // so there's no unstyled flash between inline removal and external CSS application.
  const ssrStyles = document.getElementById('ssr-styles');
  if (ssrStyles) {
    const links = [...document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]')];
    const pending = links.filter(l => !l.sheet);
    if (!pending.length) {
      ssrStyles.remove();
    } else {
      Promise.all(
        pending.map(l => new Promise<void>(res => {
          l.addEventListener('load', () => res(), { once: true });
          l.addEventListener('error', () => res(), { once: true });
        }))
      ).then(() => ssrStyles.remove());
    }
  }
});
